import { useMemo } from "react";

import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import {
  DateRange,
  DateRangePicker,
  DateRangePickerProps,
} from "src/base-components/DateRangePicker";
import { FilterPopover } from "src/base-components/FilterPopover/FilterPopover";
import {
  ConditionRenderContext,
  FieldDefinition,
  FilterFormData,
  OperatorOption,
} from "src/base-components/FilterPopover/types";
import { StringValueRenderer } from "src/base-components/FilterPopover/ValueRenderers";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { getOperatorDefinition, OPERATORS } from "src/constants/operators";
import { IconButton } from "src/design-system/Icon";
import {
  getEventProperties,
  getValidationError,
} from "src/eventsCatalogue/filters/utils";
import { useEventFilterFields } from "src/eventsCatalogue/hooks/useEventFilterFields";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";

const IS_NONE_OPTION: OperatorOption = {
  key: "is_none",
  value: OPERATORS.is_none,
};

const NUMERIC_OPS: OperatorOption[] = [
  { key: "eq", value: OPERATORS.eq },
  { key: "lt", value: OPERATORS.lt },
  { key: "le", value: OPERATORS.le },
  { key: "gt", value: OPERATORS.gt },
  { key: "ge", value: OPERATORS.ge },
  {
    key: "multiple_of",
    value: { description: "Multiple of", code: "multiple of" },
  },
  IS_NONE_OPTION,
];

// Users typically want substring match on string fields, not strict equality.
const STRING_OPS: OperatorOption[] = [
  { key: "str_contains", value: getOperatorDefinition("str_contains") },
  { key: "eq", value: OPERATORS.eq },
  { key: "starts_with", value: getOperatorDefinition("starts_with") },
  { key: "ends_with", value: getOperatorDefinition("ends_with") },
  IS_NONE_OPTION,
];

const EVENTS_OPERATOR_OVERRIDES: Record<string, OperatorOption[]> = {
  string: STRING_OPS,
  integer: NUMERIC_OPS,
  number: NUMERIC_OPS,
  url: [{ key: "contains", value: OPERATORS.contains }, IS_NONE_OPTION],
  relation: [{ key: "eq", value: OPERATORS.eq }, IS_NONE_OPTION],
};

const VALUE_RENDERERS: Record<string, React.FC<ConditionRenderContext>> = {
  url: StringValueRenderer,
  relation: StringValueRenderer,
};

type EventsFilterPopoverProps = {
  eventType: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  defaultValues?: FilterFormData;
  hasActiveFilter?: boolean;
  onApply: (formData: FilterFormData) => void;
  dateRangeValue?: DateRange;
  onDateRangeChange?: DateRangePickerProps["onChange"];
  onClickBack?: () => void;
  button?: React.ReactNode;
};

export const EventsFilterPopover: React.FC<EventsFilterPopoverProps> = ({
  eventType,
  isOpen,
  onOpenChange,
  defaultValues,
  hasActiveFilter,
  onApply,
  dateRangeValue,
  onDateRangeChange,
  onClickBack,
  button,
}) => {
  const { fields, isLoading } = useEventFilterFields(eventType);

  const { data: eventSchemas } = useEventSchemas();
  const currentEvent = useMemo(
    () => findEventSchema(eventType, eventSchemas),
    [eventType, eventSchemas],
  );

  const validateValue = useMemo(() => {
    const properties = getEventProperties(currentEvent);
    return (
      value: string,
      field: FieldDefinition | undefined,
    ): string | undefined => {
      if (!field) return undefined;
      const property = properties.find((p) => p.name === field.key);
      return getValidationError(
        property?.datatype as PropertyDefinitionOutputTypeEnum | undefined,
        value,
      );
    };
  }, [currentEvent]);

  const renderHeader = useMemo(
    () => (
      <>
        {onClickBack && (
          <IconButton icon={faArrowLeft} size="xs" onClick={onClickBack} />
        )}
        <span className="whitespace-nowrap font-inter-medium-12px text-gray-700">
          Show {currentEvent?.display_name_plural} for
        </span>
        {onDateRangeChange && (
          <span className="cursor-pointer">
            <DateRangePicker
              value={dateRangeValue}
              usePredefinedDates
              onChange={onDateRangeChange}
            />
          </span>
        )}
      </>
    ),
    [
      onClickBack,
      currentEvent?.display_name_plural,
      dateRangeValue,
      onDateRangeChange,
    ],
  );

  return (
    <FilterPopover
      button={button}
      dataLoc="events-filter-popover"
      defaultValues={defaultValues}
      fields={fields}
      hasActiveFilter={hasActiveFilter}
      isLoading={isLoading}
      isOpen={isOpen}
      maxConditionsPerGroup={5}
      maxGroups={3}
      operatorOptions={EVENTS_OPERATOR_OVERRIDES}
      renderHeader={renderHeader}
      validateValue={validateValue}
      valueRenderers={VALUE_RENDERERS}
      onApply={onApply}
      onOpenChange={onOpenChange}
    />
  );
};
